import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { formatDateTime } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchBackButton } from '@/components/ui/search-back-button';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { FilterChips } from '@/components/ui/filter-chips';
import { InlineEditCell } from '@/components/InlineEditCell';
import { ProductLinks } from '@/components/ProductLinks';
import { ProductStatusBadge } from '@/components/StatusBadge';
import { ProductDescriptionCard } from '@/components/ProductDescriptionCard';
import { TagEditor } from '@/components/TagEditor';
import { VariantEditTable, type EditableVariant } from '@/components/VariantEditTable';
import { ActivityTimeline } from '@/components/ActivityTimeline';
import { useProduct, useUpdateProductFields, usePushProduct } from '@/hooks/useProduct';
import { useUpdateInventory } from '@/hooks/useVariants';
import type { Variant, InventoryLevel } from '@/types';

const STATUS_CHIPS = [
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'archived', label: 'Archived' },
];

/** Map a detail-page `Variant` onto the shape {@link VariantEditTable} edits. */
function toEditableVariant(v: Variant): EditableVariant {
  return {
    id: v.id,
    shopify_variant_id: v.shopify_variant_id,
    shopify_product_id: v.shopify_product_id,
    shopify_inventory_item_id: v.shopify_inventory_item_id,
    sku: v.sku,
    online_price: v.price,
    nursery_price: v.nursery_price ?? null,
    nursery_only: v.nursery_only ?? null,
    inventory_quantity: v.inventory_quantity,
    option1: v.option1,
    option2: v.option2,
    option3: v.option3,
    variant_title: v.title,
    position: v.position,
  };
}

/** A labelled, click-to-edit text field on the product info card. */
function ProductTextField({
  shopifyProductId,
  field,
  label,
  value,
}: {
  shopifyProductId: number;
  field: 'title' | 'vendor' | 'product_type';
  label: string;
  value: string | null;
}) {
  const update = useUpdateProductFields();
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</label>
      <div className="mt-1">
        <InlineEditCell
          value={value}
          kind="text"
          pending={update.isPending}
          emptyLabel={`Set ${label.toLowerCase()}`}
          onCommit={(next) =>
            update.mutate(
              { shopifyProductId, fields: { [field]: next } },
              {
                onSuccess: () => toast.success(`${label} saved to Shopify`),
                onError: (err) =>
                  toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
              },
            )
          }
        />
      </div>
    </div>
  );
}

/** Click-to-edit on-hand stock for one inventory level. */
function InventoryAvailableCell({ level }: { level: InventoryLevel }) {
  const update = useUpdateInventory();
  return (
    <InlineEditCell
      value={level.available}
      kind="number"
      pending={update.isPending}
      emptyLabel="0"
      onCommit={(next) =>
        update.mutate(
          {
            inventoryItemId: level.shopify_inventory_item_id,
            available: Number(next ?? 0),
            locationId: level.shopify_location_id,
          },
          {
            onSuccess: () => toast.success('Stock saved to Shopify'),
            onError: (err) =>
              toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
          },
        )
      }
    />
  );
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useProduct(id);
  const updateProduct = useUpdateProductFields();
  const pushMutation = usePushProduct();
  // The status the user just clicked, while its save is in flight.
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);

  const product = data?.product;
  const inventoryLevels = data?.inventory_levels ?? [];

  const handlePush = () => {
    if (!product) return;
    pushMutation.mutate(product.shopify_product_id, {
      onSuccess: () => toast.success('Pushed changes to Shopify'),
      onError: (err) => toast.error(`Push failed: ${err.message}`),
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 md:px-6">
          <Skeleton className="h-8 w-8" />
          <Skeleton className="h-5 w-48" />
        </div>
        <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex h-full flex-col">
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 md:px-6">
          <SearchBackButton />
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-muted-foreground">Product not found</p>
        </div>
      </div>
    );
  }

  const productId = product.shopify_product_id;
  const variants = (product.variants ?? []).filter((variant) => !variant.is_deleted);
  const images = product.product_images ?? [];

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="flex min-h-14 shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-2 md:px-6">
        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
          <SearchBackButton />
          <h1 className="min-w-0 max-w-full flex-1 truncate text-sm font-semibold text-foreground sm:max-w-md">
            {product.title}
          </h1>
          <ProductStatusBadge status={product.status} />
          {product.has_local_edits && (
            <Badge variant="warning" className="text-[10px]">
              Unsynced edits
            </Badge>
          )}
        </div>
        <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
          <ProductLinks
            handle={product.handle}
            shopifyProductId={productId}
            withLabels
          />
          {product.has_local_edits && (
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={handlePush}
              disabled={pushMutation.isPending}
            >
              {pushMutation.isPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-1 h-4 w-4" />
              )}
              Retry push to Shopify
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 space-y-6 overflow-auto p-4 md:p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Editable product info */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="text-sm">Product Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ProductTextField
                  shopifyProductId={productId}
                  field="title"
                  label="Title"
                  value={product.title}
                />
                <ProductTextField
                  shopifyProductId={productId}
                  field="product_type"
                  label="Type"
                  value={product.product_type}
                />
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Vendor
                  </label>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {product.vendor ?? '—'}
                  </p>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Handle
                  </label>
                  <p className="mt-1 font-mono text-xs text-muted-foreground">
                    {product.handle ?? '—'}
                  </p>
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wider text-muted-foreground">
                  Status
                </label>
                <FilterChips
                  options={STATUS_CHIPS}
                  value={product.status}
                  loadingValue={pendingStatus ?? undefined}
                  disabled={updateProduct.isPending}
                  onChange={(status) => {
                    if (status === product.status) return;
                    setPendingStatus(status);
                    updateProduct.mutate(
                      { shopifyProductId: productId, fields: { status } },
                      {
                        onSuccess: () => toast.success(`Status set to ${status}`),
                        onError: (err) =>
                          toast.error(`Saved locally, but Shopify update failed: ${err.message}`),
                        onSettled: () => setPendingStatus(null),
                      },
                    );
                  }}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs uppercase tracking-wider text-muted-foreground">
                  Tags
                </label>
                <TagEditor shopifyProductId={productId} tags={product.tags ?? []} />
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Last Updated
                </label>
                <p className="mt-1 text-sm text-muted-foreground">
                  {formatDateTime(product.shopify_updated_at)}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Images (read-only) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Images</CardTitle>
            </CardHeader>
            <CardContent>
              {images.length > 0 ? (
                <div className="grid grid-cols-2 gap-2">
                  {images.map((img) => (
                    <img
                      key={img.shopify_image_id}
                      src={img.src}
                      alt={img.alt_text ?? ''}
                      className="aspect-square w-full rounded bg-muted object-cover"
                    />
                  ))}
                </div>
              ) : (
                <div className="flex h-32 items-center justify-center rounded bg-muted">
                  <p className="text-xs text-muted-foreground">No images</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Description */}
        <ProductDescriptionCard shopifyProductId={productId} bodyHtml={product.body_html} />

        {/* Variants */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Variants ({variants.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-3 sm:p-4">
            <VariantEditTable variants={variants.map(toEditableVariant)} reorderable />
          </CardContent>
        </Card>

        {/* Inventory Levels */}
        {inventoryLevels.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Inventory Levels</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Location</TableHead>
                    <TableHead>Variant</TableHead>
                    <TableHead className="w-[140px]">Available</TableHead>
                    <TableHead>Last Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {inventoryLevels.map((level) => (
                    <TableRow key={level.id}>
                      <TableCell className="text-sm">
                        {level.location_name ?? `Location ${level.shopify_location_id}`}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {level.sku ?? level.variant_title ?? '—'}
                      </TableCell>
                      <TableCell>
                        <InventoryAvailableCell level={level} />
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {formatDateTime(level.shopify_updated_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Activity timeline */}
        <ActivityTimeline productId={productId} />
      </div>
    </div>
  );
}
